<template>
  <div class="shoplazza-page">
    <page-background />
    <div class="page-content">
      <top-nav :store-name="storeName" :platform-label="platformLabel" />
      <div class="page-body">
        <div class="select-outer">
          <select-app-card
            :merchant-id="merchantId"
            :apps="appList"
            :selected-app="selectedApp"
            :loading="loading"
            :confirming="confirming"
            :regions="regions"
            :platform-label="platformLabel"
            @select="handleSelect"
            @confirm="handleConfirm"
          />
        </div>
      </div>
    </div>

    <credentials-modal
      :visible="showCredentialsModal"
      :app-name="authResult.app_name"
      :store-name="authResult.shop_name || storeName"
      :mer-no="authResult.mer_no"
      :sign-key="authResult.sign_key"
      :app-key="authResult.app_key"
      :redirect="authResult.redirect"
      :already-bound="alreadyBound"
      :platform="platform"
      :platform-label="platformLabel"
      @close="showCredentialsModal = false"
      @open-settings="handleOpenSettings"
    />
  </div>
</template>

<script>
import { request } from '@/utils/utils'
import PageBackground from './components/PageBackground.vue'
import TopNav from './components/TopNav.vue'
import SelectAppCard from './components/SelectAppCard.vue'
import CredentialsModal from './components/CredentialsModal.vue'

export default {
  name: 'ShoplazzaSelectApp',
  components: {
    PageBackground,
    TopNav,
    SelectAppCard,
    CredentialsModal,
  },
  data () {
    return {
      appList: [],
      regions: [],
      selectedApp: null,
      loading: false,
      confirming: false,
      showCredentialsModal: false,
      alreadyBound: false,
      storeName: '',
      authResult: {
        redirect: '',
        mer_no: '',
        sign_key: '',
        app_key: '',
        app_id: '',
        app_name: '',
        shop_name: '',
      },
    }
  },
  computed: {
    merchantId () {
      return localStorage.getItem('merchantNo') || ''
    },
    authState () {
      return (this.$route.query && this.$route.query.state) || ''
    },
    platform () {
      const prefix = String(this.authState || '').split('_')[0].toLowerCase()
      if (prefix === 'shopify' || prefix === 'shoplazza') return prefix
      return ''
    },
    platformLabel () {
      if (this.platform === 'shopify') return 'Shopify'
      if (this.platform === 'shoplazza') return 'Shoplazza'
      return ''
    },
    amzAccessToken () {
      const query = this.$route.query || {}
      return (
        query['x-amz-access-token'] ||
        query.x_amz_access_token ||
        query.amz_access_token ||
        ''
      )
    },
  },
  created () {
    this.fetchStoreState()
    this.fetchData()
  },
  methods: {
    buildPluginHeaders () {
      const token = localStorage.getItem('Authorization')
      const headers = {
        'Content-Type': 'application/json',
        access_token: token,
      }
      if (this.amzAccessToken) {
        headers['x-amz-access-token'] = this.amzAccessToken
      }
      return headers
    },
    isAuthorizeTimeoutResponse (body) {
      return body && body.code === '20001'
    },
    handleAuthorizeTimeout (body) {
      if (!this.isAuthorizeTimeoutResponse(body)) return false
      this.$message({
        type: 'error',
        message: body.msg || 'authorize timeout.',
      })
      return true
    },
    fetchStoreState () {
      const token = localStorage.getItem('Authorization')
      if (!token || !this.authState) return

      request({
        method: 'post',
        url: `api/dashboard/plugin/get-state?state=${encodeURIComponent(this.authState)}`,
        noQs: true,
        headers: this.buildPluginHeaders(),
        data: {},
      })
        .then((res) => {
          const body = res && res.data ? res.data : {}
          if (this.handleAuthorizeTimeout(body)) return
          if (body.code && body.code !== '10000') return
          const payload = body.data || {}
          this.storeName = payload.shop_name || ''
        })
        .catch(() => {
          // store name is optional for page rendering
        })
    },
    fetchData () {
      const token = localStorage.getItem('Authorization')
      if (!token) return

      this.loading = true
      Promise.all([
        request({
          method: 'post',
          url: `api/dashboard/merchant/getMerchantInfo?access_token=${token}`,
        }),
        request({
          method: 'post',
          url: `api/dashboard/basic/getDropdown?access_token=${token}`,
        }),
      ])
        .then(([merchantRes, dropdownRes]) => {
          this.appList = (merchantRes.data && merchantRes.data.app_list) || []
          this.regions = (dropdownRes.data && dropdownRes.data.regions) || []
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSelect (app) {
      this.selectedApp = app
    },
    handleConfirm () {
      if (!this.selectedApp || this.selectedApp.status === 0 || this.confirming) return

      if (!this.authState) {
        this.$message({
          type: 'error',
          message: 'Missing state parameter in URL',
        })
        return
      }

      const token = localStorage.getItem('Authorization')
      if (!token) return

      this.confirming = true

      request({
        method: 'post',
        url: 'api/dashboard/plugin/auth',
        noQs: true,
        headers: this.buildPluginHeaders(),
        data: {
          state: this.authState,
          app_id: String(this.selectedApp.app_id),
        },
      })
        .then((res) => {
          const body = res && res.data ? res.data : {}
          const payload = body.data || {}
          const failed =
            body.fail === true ||
            body.success === false ||
            (body.code && body.code !== '10000')

          if (this.handleAuthorizeTimeout(body)) return

          if (failed && !payload.mer_no && !payload.redirect) {
            this.$message({
              type: 'error',
              message: body.msg || body.message || 'Authorization failed',
            })
            return
          }

          this.authResult = {
            redirect: payload.redirect || '',
            mer_no: payload.mer_no || '',
            sign_key: payload.sign_key || payload.sing_key || '',
            app_key: payload.app_key || '',
            app_id: payload.app_id || String(this.selectedApp.app_id),
            app_name: payload.app_name || this.selectedApp.app_name || '',
            shop_name: payload.shop_name || this.storeName || '',
          }
          // 后端不会返回 already_bound：全部按“第一次绑定”处理
          this.alreadyBound = false
          this.showCredentialsModal = true
        })
        .catch(() => {
          this.$message({
            type: 'error',
            message: 'Authorization request failed',
          })
        })
        .finally(() => {
          this.confirming = false
        })
    },
    handleOpenSettings () {
      // 保持当前页不动，不关闭弹框；只打开平台设置新页面
      this.openExternalSettings()
    },
    openExternalSettings () {
      const url = this.authResult.redirect
      if (!url) {
        this.$message({
          type: 'error',
          message: 'Missing redirect url',
        })
        return
      }
      window.open(url, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './styles/variables.scss';

.shoplazza-page {
  position: relative;
  min-height: 100vh;
  color: $ink;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.page-content {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-body {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 24px 60px;
}

.select-outer {
  width: 900px;
  max-width: 100%;
}
</style>
